import { useEffect, useState } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { WorkflowValidator, ValidationError } from '@/services/validation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';

export function ValidationPanel() {
  const { currentWorkflow, selectNode } = useWorkflowStore();
  const [errors, setErrors] = useState<ValidationError[]>([]);

  useEffect(() => {
    if (currentWorkflow) {
      const validationErrors = WorkflowValidator.validate(currentWorkflow);
      setErrors(validationErrors);
    }
  }, [currentWorkflow]);

  if (!currentWorkflow) return null;

  const errorCount = errors.filter((e) => e.type === 'error').length;
  const warningCount = errors.filter((e) => e.type === 'warning').length;

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-foreground">Validation</h4>
        {errorCount === 0 && warningCount === 0 && (
          <span className="flex items-center gap-1 text-xs text-green-600">
            <CheckCircle className="w-4 h-4" />
            All checks passed
          </span>
        )}
      </div>

      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map((error, idx) => (
            <div
              key={idx}
              className={`p-3 rounded border-l-4 space-y-1 ${
                error.type === 'error'
                  ? 'bg-destructive/10 border-destructive/50 text-destructive'
                  : 'bg-amber-500/10 border-amber-500/50 text-amber-700'
              }`}
            >
              <div className="flex items-start gap-2">
                {error.type === 'error' ? (
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                )}
                <p className="text-xs font-medium flex-1">{error.message}</p>
              </div>
              {error.nodeId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => selectNode(error.nodeId!)}
                >
                  Go to node
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {errors.length === 0 && (
        <p className="text-xs text-muted-foreground">No validation issues found.</p>
      )}

      <div className="pt-2 border-t border-border grid grid-cols-2 gap-2 text-center text-xs">
        <div>
          <p className="text-muted-foreground">Errors</p>
          <p className="font-semibold text-destructive">{errorCount}</p>
        </div>
        <div>
          <p className="text-muted-foreground">Warnings</p>
          <p className="font-semibold text-amber-600">{warningCount}</p>
        </div>
      </div>
    </Card>
  );
}
